import { Component, OnInit } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { Router } from '@angular/router'
import { AccountDto, SyncPairDto } from 'src/app/services/dtos'
import { handleArpioError } from 'src/utils/handle-error'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import { ViewableApplicationResource } from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { ResourcesTab } from 'src/app/account/components/application/application.component'
import { throwError } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { buildAllMockIssues, buildNestedIssues, buildStatusResources } from './test-page-mock-data'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'

const fakeSyncPair = {
  sourceAwsAccountId: '111111',
  sourceRegion: 'aws-source-1',
  targetAwsAccountId: '222222',
  targetRegion: 'aws-target-1',
  sourceEndpointUri: 'fake-source-endpoint',
  targetEndpointUri: 'fake-target-endpoint',
} as SyncPairDto

@Component({
  selector: 'app-test-page',
  templateUrl: './test-page.component.html',
  styleUrls: ['./test-page.component.scss'],
  standalone: false,
})
export class TestPageComponent implements OnInit {
  accounts: Array<AccountDto> = null
  loading = true
  syncPair: SyncPair = null
  application: Application = null
  issues: ViewableIssue[] = []
  nestedIssues: ViewableIssue[] = []
  statusResources: ViewableApplicationResource[] = []
  RecoveryTab: ResourcesTab.RECOVERY

  readonly ACCOUNTS_PREVIEW = 6
  showAllAccounts = false
  expandedSections: { [key: string]: boolean } = { accounts: true, issues: true, nestedIssues: true, resources: true }

  get visibleAccounts(): Array<AccountDto> {
    if (!this.accounts) return []
    return this.showAllAccounts ? this.accounts : this.accounts.slice(0, this.ACCOUNTS_PREVIEW)
  }

  get hasMoreAccounts(): boolean {
    return this.accounts?.length > this.ACCOUNTS_PREVIEW
  }

  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section]
  }

  constructor(
    private uiService: UiService,
    private apiService: ApiService,
    private router: Router,
    private applicationProviderService: ApplicationProviderService
  ) {}

  ngOnInit(): void {
    /*
     * Do one API call to:
     *  a) Check that the user is logged in. We don't want bots and whatknot to have access to
     *     this page.
     *  b) Make this page a useful jumping off point for debugging actual accounts.
     */

    this.statusResources = buildStatusResources()
    this.issues = buildAllMockIssues()
    this.nestedIssues = buildNestedIssues()

    this.apiService
      .listAccounts()
      .pipe(
        switchMap((accounts) => {
          if (accounts) {
            this.accounts = accounts
            // Find the first non-empty account
            const firstNonEmpty = accounts.find((acc) => !!acc && !!acc.accountId)
            if (firstNonEmpty) {
              return this.apiService.listSyncPairs(firstNonEmpty.accountId)
            } else {
              return throwError('No valid accounts found')
            }
          } else {
            return throwError('Not logged in to any accounts :(')
          }
        })
      )
      .subscribe(
        (syncPairs) => {
          const accountId = this.accounts[0].accountId
          // Check if syncPairs is an array and has at least one valid SyncPairDto
          if (Array.isArray(syncPairs) && syncPairs.length > 0 && syncPairs[0] && syncPairs[0].sourceAwsAccountId) {
            this.syncPair = new SyncPair(this.apiService, accountId, syncPairs[0])
          } else {
            console.warn('No valid sync pairs found:', syncPairs)
            // Optionally use a fakeSyncPair or handle the empty case
            this.syncPair = new SyncPair(this.apiService, accountId, fakeSyncPair)
          }

          // Load applications to pass to issue components that need one
          this.applicationProviderService.loadAllApplications$(accountId).subscribe(
            () => {
              const apps = this.applicationProviderService.loadedApplications()
              if (apps.length > 0) {
                this.application = apps[0]
              }
              this.loading = false
            },
            (err) => {
              console.warn('Failed to load applications:', err)
              this.loading = false
            }
          )
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  scrollTo(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  doLogout() {
    this.apiService
      .logout()
      .subscribe(
        (nothing) => console.log('Logged out.'),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }
}
